import React, { useState, useRef } from "react";
import { Button, Input, FormGroup, Label, Progress, Alert } from "reactstrap";
import MPSButton from "../../Buttons/Buttons/MPSButton";
import ModalPopup from "../../ModalPopup";
import fileService from "../../../services/fileService";
import Messages from "../../../Shared/Messages";
import Text from "../../Text";
import I18nProvider from "../../../services/I18nProvider";

const intl = I18nProvider.getIntlProvider();

/**
 * Enhanced image insertion component for TipTap editor
 * Supports URL input, file upload, and drag & drop
 */
const EnhancedImageButton = ({ editor, onClose }) => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("upload"); // "url", "upload"
  const [imageUrl, setImageUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setImageUrl("");
    setUploadProgress(0);
    setUploading(false);
    setError("");
    setPreview(null);
    setActiveTab("upload");
  };

  const handleClose = () => {
    setShowModal(false);
    resetState();
    if (onClose) onClose();
  };

  const validateImageUrl = (url) => {
    try {
      new URL(url);
      // Check if it's likely an image URL
      const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
      return imageExtensions.test(url) || url.includes('base64');
    } catch {
      return false;
    }
  };

  const insertImageFromUrl = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!imageUrl.trim()) {
      setError("Please enter an image URL");
      return;
    }

    if (!validateImageUrl(imageUrl)) {
      setError("Please enter a valid image URL");
      return;
    }

    // Insert image into editor
    editor.chain().focus().setImage({ 
      src: imageUrl,
      width: 400, // Default width
      height: null // Let height auto-adjust
    }).run();

    handleClose();
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image file must be smaller than 10MB");
      return;
    }

    setError("");
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview({
        url: e.target.result,
        name: file.name,
        size: file.size
      });
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const contents = await fileService.uploadFiles("IMAGE", file, [], true);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Insert uploaded image
      if (contents && contents[0]) {
        editor.chain().focus().setImage({ 
          src: contents[0].contentURL,
          width: 400,
          height: null
        }).run();

        // Close modal after successful upload
        setTimeout(() => {
          handleClose();
        }, 500);
      }

    } catch (error) {
      console.error("Upload error:", error);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const renderUrlTab = () => (
    <div>
      <FormGroup>
        <Label for="imageUrl">Image URL</Label>
        <Input
          id="imageUrl"
          type="url"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="https://example.com/image.jpg"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              insertImageFromUrl(e);
            }
          }}
        />
        <small className="text-muted">
          Enter a direct link to an image (JPG, PNG, GIF, WebP, SVG)
        </small>
      </FormGroup>

      {imageUrl && validateImageUrl(imageUrl) && (
        <div className="mb-3">
          <Label>Preview:</Label>
          <div className="border rounded p-2 text-center">
            <img 
              src={imageUrl} 
              alt="Preview" 
              style={{ maxWidth: "100%", maxHeight: "200px" }}
              onError={() => setError("Could not load image from URL")}
              onLoad={() => setError("")}
            />
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end gap-2">
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <MPSButton 
          color="primary" 
          onClick={(e) => insertImageFromUrl(e)}
          disabled={!imageUrl.trim() || !validateImageUrl(imageUrl)}
        >
          <i className="fa fa-plus mr-2" />
          Insert Image
        </MPSButton>
      </div>
    </div>
  );

  const renderUploadTab = () => (
    <div>
      <div 
        className="border-dashed border rounded p-4 text-center mb-3"
        style={{ 
          borderStyle: "dashed", 
          borderWidth: "2px", 
          borderColor: "#dee2e6",
          backgroundColor: "#f8f9fa"
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = "#007bff";
          e.currentTarget.style.backgroundColor = "#e3f2fd";
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = "#dee2e6";
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = "#dee2e6";
          e.currentTarget.style.backgroundColor = "#f8f9fa";
          
          const files = e.dataTransfer.files;
          if (files && files[0]) {
            handleFileSelect(files[0]);
          }
        }}
      >
        <i className="fa fa-cloud-upload-alt fa-3x text-muted mb-3" />
        <div>
          <strong>Click to browse</strong> or drag & drop an image here
        </div>
        <small className="text-muted">
          Supports JPG, PNG, GIF, WebP • Max 10MB
        </small>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {uploading && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} color="primary" />
        </div>
      )}

      {preview && (
        <div className="mb-3">
          <Label>Preview:</Label>
          <div className="border rounded p-2">
            <div className="row align-items-center">
              <div className="col-auto">
                <img 
                  src={preview.url} 
                  alt="Preview" 
                  style={{ width: "80px", height: "80px", objectFit: "cover" }}
                  className="rounded"
                />
              </div>
              <div className="col">
                <div><strong>{preview.name}</strong></div>
                <small className="text-muted">
                  {(preview.size / 1024 / 1024).toFixed(2)} MB
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end">
        <Button color="secondary" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="tip-button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowModal(true);
        }}
        title="Insert Image"
        tabIndex="-1"
        type="button"
      >
        <i className="fa fa-image" />
      </button>

      {showModal && (
        <ModalPopup
          title="Insert Image"
          onClose={handleClose}
          size="md"
        >
          <div>
            {/* Tab Navigation */}
            <div className="nav nav-tabs mb-3">
              <button
                className={`nav-link ${activeTab === "upload" ? "active" : ""}`}
                onClick={() => setActiveTab("upload")}
              >
                <i className="fa fa-upload mr-2" />
                Upload File
              </button>
              <button
                className={`nav-link ${activeTab === "url" ? "active" : ""}`}
                onClick={() => setActiveTab("url")}
              >
                <i className="fa fa-link mr-2" />
                From URL
              </button>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert color="danger" className="mb-3">
                <i className="fa fa-exclamation-triangle mr-2" />
                {error}
              </Alert>
            )}

            {/* Tab Content */}
            {activeTab === "url" && renderUrlTab()}
            {activeTab === "upload" && renderUploadTab()}
          </div>
        </ModalPopup>
      )}
    </>
  );
};

export default EnhancedImageButton;